import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext();

const storageNamespace = siteConfig.brandName
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "");
const CART_STORAGE_KEY = `${storageNamespace}:cart:v1`;
const WISHLIST_STORAGE_KEY = `${storageNamespace}:wishlist:v1`;

const findCatalogProduct = (productId) =>
  productData.find((product) => String(product.id) === String(productId));

const readStoredArray = (key, normalizeItem) => {
  if (typeof window === "undefined") return [];

  try {
    const savedValue = window.localStorage.getItem(key);
    const parsedValue = savedValue ? JSON.parse(savedValue) : [];
    return Array.isArray(parsedValue)
      ? parsedValue.map(normalizeItem).filter(Boolean)
      : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing or when the quota is full.
  }
};

const resolveProductId = (productOrId) =>
  productOrId && typeof productOrId === "object"
    ? productOrId.id
    : productOrId;

const idsMatch = (firstId, secondId) =>
  String(firstId) === String(secondId);

const normalizeQuantity = (quantity, maximum = Number.POSITIVE_INFINITY) => {
  const parsedQuantity = Number(quantity);
  const normalized = Number.isFinite(parsedQuantity)
    ? Math.max(1, Math.floor(parsedQuantity))
    : 1;
  return Math.min(normalized, maximum);
};

const normalizeCartItem = (item) => {
  if (!item || typeof item !== "object") return null;

  const product = findCatalogProduct(item.id);
  const stock = Number(product?.stock || 0);
  if (!product || stock <= 0) return null;

  const size = typeof item.size === "string" ? item.size : "";
  const color = typeof item.color === "string" ? item.color : "";
  if (!product.size?.includes(size) || !product.colors?.includes(color)) {
    return null;
  }

  return {
    id: product.id,
    size,
    color,
    quantity: normalizeQuantity(item.quantity, stock),
  };
};

const normalizeWishlistItem = (productId) => {
  const product = findCatalogProduct(productId);
  return product ? product.id : null;
};

const resolveVariant = (productOrId, options, size, color) => {
  const product =
    productOrId && typeof productOrId === "object" ? productOrId : {};
  const optionValues =
    options && typeof options === "object" ? options : {};
  const productSize =
    typeof product.size === "string" ? product.size : product.selectedSize;
  const productColor =
    typeof product.color === "string" ? product.color : product.selectedColor;

  return {
    id: resolveProductId(productOrId),
    quantity: normalizeQuantity(
      typeof options === "number"
        ? options
        : optionValues.quantity ?? product.quantity ?? 1
    ),
    size: size ?? optionValues.size ?? productSize ?? "",
    color: color ?? optionValues.color ?? productColor ?? "",
  };
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(CART_STORAGE_KEY, normalizeCartItem)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY, normalizeWishlistItem)
  );

  useEffect(() => {
    persistArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addCart = useCallback((productOrId, options, size, color) => {
    const requestedItem = resolveVariant(productOrId, options, size, color);
    const product = findCatalogProduct(requestedItem.id);
    if (!product || Number(product.stock) <= 0) return;

    const nextItem = normalizeCartItem({
      ...requestedItem,
      size: requestedItem.size || product.size?.[0] || "",
      color: requestedItem.color || product.colors?.[0] || "",
    });
    if (!nextItem) return;

    setCartItems((currentItems) => {
      const matchingItem = currentItems.find(
        (item) =>
          idsMatch(item.id, nextItem.id) &&
          item.size === nextItem.size &&
          item.color === nextItem.color
      );

      if (!matchingItem) return [...currentItems, nextItem];

      return currentItems.map((item) =>
        idsMatch(item.id, nextItem.id) &&
        item.size === nextItem.size &&
        item.color === nextItem.color
          ? {
              ...item,
              quantity: normalizeQuantity(
                normalizeQuantity(item.quantity) + nextItem.quantity,
                Number(product.stock)
              ),
            }
          : item
      );
    });
  }, []);

  const removeCart = useCallback((productOrId, size, color) => {
    const variant = resolveVariant(productOrId, undefined, size, color);

    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(
            idsMatch(item.id, variant.id) &&
            item.size === variant.size &&
            item.color === variant.color
          )
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productOrId, size, color, quantity) => {
      const productId = resolveProductId(productOrId);
      const product = findCatalogProduct(productId);
      if (!product || Number(product.stock) <= 0) return;
      const nextQuantity = normalizeQuantity(quantity, Number(product.stock));

      setCartItems((currentItems) =>
        currentItems.map((item) =>
          idsMatch(item.id, productId) &&
          item.size === size &&
          item.color === color
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addToWishlist = useCallback((productOrId) => {
    const productId = resolveProductId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((currentItems) =>
      currentItems.some((id) => idsMatch(id, productId))
        ? currentItems
        : [...currentItems, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const productId = resolveProductId(productOrId);
    setWishlistItems((currentItems) =>
      currentItems.filter((id) => !idsMatch(id, productId))
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const productId = resolveProductId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((currentItems) =>
      currentItems.some((id) => idsMatch(id, productId))
        ? currentItems.filter((id) => !idsMatch(id, productId))
        : [...currentItems, productId]
    );
  }, []);

  const isWishlisted = useCallback(
    (productOrId) => {
      const productId = resolveProductId(productOrId);
      return wishlistItems.some((id) => idsMatch(id, productId));
    },
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlist: wishlistItems,
      wishlistItems,
      cartCount: cartItems.reduce(
        (total, item) => total + normalizeQuantity(item.quantity),
        0
      ),
      wishlistCount: wishlistItems.length,
      addCart,
      addToCart: addCart,
      removeCart,
      removeFromCart: removeCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addCart,
      addToWishlist,
      cartItems,
      clearCart,
      isWishlisted,
      removeCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
